import { ApiResponse } from '../types';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const TIMEOUT_MS = 100000;

export interface FetchResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const azureToken = localStorage.getItem('azureAuthToken');
  const jwtToken = localStorage.getItem('authToken');
  const token = azureToken || jwtToken;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function request<T>(
  url: string,
  options: RequestInit & { responseType?: string } = {}
): Promise<FetchResponse<T>> {
  const { responseType, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  const isFormData = fetchOptions.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...getAuthHeaders(),
  };

  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('azureAuthToken');
      window.location.href = '/login';
      throw Object.assign(new Error('Unauthorized'), {
        response: {
          data: {},
          status: 401,
          statusText: response.statusText,
          headers: response.headers,
        },
      });
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error(errorData.message || `Request failed with status ${response.status}`),
        {
          response: {
            data: errorData,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          },
        }
      );
    }

    let data: T;
    if (responseType === 'blob') {
      data = (await response.blob()) as unknown as T;
    } else {
      data = await response.json();
    }

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${TIMEOUT_MS}ms`);
    }
    throw error;
  }
}

export const apiClient = {
  get: async <T = any>(url: string, config?: any): Promise<FetchResponse<T>> => {
    return request<T>(url, {
      method: 'GET',
      responseType: config?.responseType,
    });
  },

  post: async <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    const response = await request<ApiResponse<T>>(url, {
      method: 'POST',
      body,
    });
    return response.data;
  },

  put: async <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    const response = await request<ApiResponse<T>>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  delete: async <T = any>(url: string): Promise<ApiResponse<T>> => {
    const response = await request<ApiResponse<T>>(url, {
      method: 'DELETE',
    });
    return response.data;
  },

  patch: async <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    const response = await request<ApiResponse<T>>(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return response.data;
  },
};

export default apiClient;
